import { useRef } from 'react';

import { FeedbackCard } from '@hotjar/feedback';
import type { FeedbackCardSentiment } from '@hotjar/feedback';
import { trackClick, viewElement } from '@hotjar/tracking';
import type { Site } from '@hotjar/site';
import { capitalize } from '@hotjar/insights-common/lib/string';

import { isSinglePageSearch } from '../../utils/isSinglePageSearch';
import { EVENT_UI_POSITIONS } from '../../constants-types/TrackEventUIPositions';
import { EVENT_NAMES } from '../../constants-types/TrackEventNames';
import { getTrackGroupDataAndTriggeredOnState } from '../../utils/trackingUtils';
import { SmartPanelSection, SmartPanelSegment } from './SmartPanelBlocks';

import type { TrackableSite } from '../../constants-types/ExternalDependencyTypes';

interface FeedbackSectionProps {
  currentSite?: TrackableSite;
  selectedTargetingValue: TargetingValue;
  pageUrl: string;
}

export const FeedbackSection = ({
  currentSite,
  selectedTargetingValue,
  pageUrl,
}: FeedbackSectionProps): JSX.Element | null => {
  const viewRef = useRef<boolean>(false);

  const isSinglePage = isSinglePageSearch(selectedTargetingValue);

  if (!currentSite || !isSinglePage) return null;

  const trackCard = (eventType: 'view' | 'click', cardSentiment: FeedbackCardSentiment) => {
    if (currentSite !== undefined) {
      const eventValues = {
        name: `${capitalize(cardSentiment.toString())} ${EVENT_NAMES.FEEDBACK_CARD}`,
        uiPosition: EVENT_UI_POSITIONS.SMART_PANEL,
        ...getTrackGroupDataAndTriggeredOnState(currentSite),
      };

      if (eventType === 'view') {
        viewElement(eventValues);
      } else {
        trackClick(eventValues);
      }
    }
  };

  const handleRatingSet = (cardSentiment: FeedbackCardSentiment) => {
    if (!viewRef.current) {
      trackCard('view', cardSentiment);
      viewRef.current = true;
    }
  };

  const handleCardClick = (cardSentiment: FeedbackCardSentiment) => {
    trackCard('click', cardSentiment);
  };

  return (
    <SmartPanelSection>
      <SmartPanelSegment>
        <FeedbackCard
          site={currentSite as Site}
          pageUrl={pageUrl}
          pageOperation={selectedTargetingValue === 'exact' ? 'exact' : 'simple_match'}
          onRatingSet={handleRatingSet}
          onClick={handleCardClick}
        />
      </SmartPanelSegment>
    </SmartPanelSection>
  );
};
